import { Link } from "react-router-dom";
import { Shield, Cookie, Eye, Lock, Mail, Database, Clock, UserCheck, FileText, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import useSEO from "@/hooks/useSEO";

export default function PrivacyPolicy() {
  useSEO();

  const sections = [
    {
      id: "information-collected",
      icon: Database,
      title: "Information We Collect",
      content: [
        {
          subtitle: "Information You Provide",
          text: "When you use our contact forms, request a roof inspection, or download guides, we collect information you voluntarily provide, including:",
          list: [
            "Name and contact information (email, phone number)",
            "Property address and city",
            "Information about your roofing situation or insurance claim",
            "Any other information you choose to provide in form submissions"
          ]
        },
        {
          subtitle: "Information Collected Automatically",
          text: "When you visit our website, we may automatically collect certain information, including:",
          list: [
            "IP address and general location (city/state level)",
            "Browser type and device information",
            "Pages visited and time spent on pages",
            "Referring website or search terms used to find us"
          ]
        }
      ]
    },
    {
      id: "how-we-use",
      icon: Eye,
      title: "How We Use Your Information",
      content: [
        {
          text: "We use the information we collect to:",
          list: [
            "Respond to your inquiries and provide requested services",
            "Connect you with qualified roofing contractors in your area",
            "Send you requested guides and educational materials",
            "Improve our website and services",
            "Analyze website traffic and usage patterns",
            "Comply with legal obligations"
          ]
        }
      ]
    },
    {
      id: "cookies",
      icon: Cookie,
      title: "Cookies and Tracking Technologies",
      content: [
        {
          subtitle: "Types of Cookies We Use",
          text: "Our website uses cookies and similar tracking technologies:",
          list: [
            "Necessary Cookies: Essential for website functionality (always active)",
            "Analytics Cookies: Help us understand how visitors use our site (Google Analytics)",
            "Marketing Cookies: Used to deliver relevant content and measure campaign effectiveness"
          ]
        },
        {
          subtitle: "Managing Your Cookie Preferences",
          text: "You can manage your cookie preferences at any time by clicking the \"Cookie Settings\" link in our website footer. You can also configure your browser to block cookies, though this may affect website functionality."
        }
      ]
    },
    {
      id: "sharing",
      icon: UserCheck,
      title: "Information Sharing",
      content: [
        {
          text: "We may share your information with:",
          list: [
            "Roofing contractors in your area (only when you request to be connected)",
            "Service providers who help us operate our website (hosting, email services)",
            "Analytics providers (in anonymized/aggregated form)",
            "Legal authorities when required by law"
          ]
        },
        {
          text: "We do NOT sell your personal information to third parties for marketing purposes."
        }
      ]
    },
    {
      id: "security",
      icon: Lock,
      title: "Data Security",
      content: [
        {
          text: "We implement appropriate technical and organizational measures to protect your personal information, including:",
          list: [
            "Encryption of data in transit (HTTPS)",
            "Secure storage of form submissions",
            "Limited access to personal information on a need-to-know basis",
            "Regular security assessments"
          ]
        },
        {
          text: "However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security."
        }
      ]
    },
    {
      id: "retention",
      icon: Clock,
      title: "Data Retention",
      content: [
        {
          text: "We retain your personal information for as long as necessary to:",
          list: [
            "Provide the services you requested",
            "Comply with legal obligations",
            "Resolve disputes and enforce our agreements"
          ]
        },
        {
          text: "Contact form submissions are typically retained for 2 years unless you request deletion sooner."
        }
      ]
    },
    {
      id: "rights",
      icon: FileText,
      title: "Your Rights",
      content: [
        {
          text: "Depending on your location, you may have certain rights regarding your personal information:",
          list: [
            "Access: Request a copy of the personal information we hold about you",
            "Correction: Request correction of inaccurate information",
            "Deletion: Request deletion of your personal information",
            "Opt-out: Unsubscribe from marketing communications",
            "Cookie preferences: Manage your cookie settings at any time"
          ]
        },
        {
          text: "To exercise any of these rights, please contact us using the information below."
        }
      ]
    },
    {
      id: "third-party",
      icon: ExternalLink,
      title: "Third-Party Links",
      content: [
        {
          text: "Our website may contain links to third-party websites, including insurance company claim portals. We are not responsible for the privacy practices of these external sites. We encourage you to review the privacy policies of any third-party sites you visit."
        }
      ]
    },
    {
      id: "children",
      icon: Shield,
      title: "Children's Privacy",
      content: [
        {
          text: "Our website is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately."
        }
      ]
    },
    {
      id: "changes",
      icon: Clock,
      title: "Changes to This Policy",
      content: [
        {
          text: "We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the \"Last Updated\" date. Your continued use of our website after any changes constitutes acceptance of the updated policy."
        }
      ]
    }
  ];

  return (
    <div data-testid="privacy-policy-page">
      {/* Hero Section */}
      <section className="bg-navy py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-orange/20 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-orange" />
            </div>
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">
                Privacy Policy
              </h1>
              <p className="text-gray-400 text-sm">Last Updated: December 15, 2025</p>
            </div>
          </div>
          
          <p className="text-gray-300 leading-relaxed">
            Minnesota Storm Damage Resource Center (&ldquo;StormCare,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) 
            is committed to protecting your privacy. This Privacy Policy explains how we collect, use, 
            disclose, and safeguard your information when you visit our website stormcaremn.com.
          </p>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="bg-gray-100 py-6 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <p className="text-sm text-gray-600 mb-3">Jump to section:</p>
          <div className="flex flex-wrap gap-2">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="text-sm bg-white border border-gray-200 px-3 py-1.5 rounded-lg hover:border-orange hover:text-orange transition-colors"
              >
                {section.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="space-y-12">
            {sections.map((section) => (
              <div key={section.id} id={section.id} className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange/10 rounded-lg flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-orange" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold text-navy">
                    {section.title}
                  </h2>
                </div>
                
                <div className="pl-13 space-y-4">
                  {section.content.map((item, idx) => (
                    <div key={idx}>
                      {item.subtitle && (
                        <h3 className="font-medium text-navy mb-2">{item.subtitle}</h3>
                      )}
                      {item.text && (
                        <p className="text-gray-600 leading-relaxed mb-3">{item.text}</p>
                      )}
                      {item.list && (
                        <ul className="space-y-2 ml-4">
                          {item.list.map((listItem, listIdx) => (
                            <li key={listIdx} className="text-gray-600 flex items-start gap-2">
                              <span className="w-1.5 h-1.5 bg-orange rounded-full mt-2 flex-shrink-0" />
                              {listItem}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="bg-white border border-gray-200 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-orange" />
              <h2 className="font-heading text-xl font-semibold text-navy">
                Contact Us About Privacy
              </h2>
            </div>
            
            <p className="text-gray-600 mb-6">
              If you have questions about this Privacy Policy or wish to exercise your privacy rights, 
              please contact us:
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-navy font-medium">Minnesota Storm Damage Resource Center</p>
              <p className="text-gray-600">Email: privacy@stormcaremn.com</p>
              <p className="text-gray-600">Website: stormcaremn.com</p>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/">
                <Button className="bg-orange hover:bg-orange-hover text-white">
                  Return to Home
                </Button>
              </Link>
              <Link to="/faqs">
                <Button variant="outline" className="border-gray-300">
                  View FAQs
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-6 bg-gray-100">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <p className="text-xs text-gray-500 text-center">
            This Privacy Policy is provided for informational purposes and does not constitute legal advice. 
            For specific legal questions regarding privacy compliance, please consult with a qualified attorney.
          </p>
        </div>
      </section>
    </div>
  );
}
